import React, { useEffect, useState } from "react";
import instance from "../../../../axios/instance";
import { getToken } from "../../../../utils/token";
import {
  useLoaderData,
  useNavigate,
  useRevalidator,
  useSearchParams,
} from "react-router-dom";
import EarningsChart from "../components/EarningsChart";
import { ArrowLeft2, ArrowRight2, SearchNormal, Sort } from "iconsax-react";
import { PaginationButton } from "../../../../components/PaginationButton";
import dayjs from "dayjs";

export const subscriptionsLoader = async () => {
  const token = getToken();
  const subscriptions = await instance.get("/doctor/connect/subscriptions", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return {
    subscriptions: subscriptions.data,
  };
};

export const SubscriptionsPage = () => {
  const { subscriptions } = useLoaderData();
  const [filteredSubscriptions, setFilteredSubscriptions] =
    useState(subscriptions);
  const [_subscriptions, setSubscriptions] = useState(
    [...subscriptions].map((_, index) => ({
      ..._,
      index: index + 1,
    }))
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q");
  const [searchQuery, setSearchQuery] = useState(query || "");
  const [pageIndex, setPageIndex] = useState(1);
  const [sorted, setSorted] = useState("");
  const navigate = useNavigate();

  const filterSubscriptions = (subscriptions) => {
    setFilteredSubscriptions((items) =>
      subscriptions.filter((_, index) => index + 1 <= 10)
    );
  };

  const handleSearchQueries = (subscriptions) => {
    return subscriptions.filter((subscription) => {
      const id = `${subscription.id}` === query;
      const status = subscription.status.toLowerCase().includes(query);
      const customerEmail = subscription.payer_name
        ?.toLowerCase()
        .includes(query);

      return id || status || customerEmail;
    });
  };

  const handleSearch = () => {
    setFilteredSubscriptions(() => {
      return handleSearchQueries(_subscriptions);
    });
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const query = searchQuery.toLowerCase();
    setSearchParams({ q: query });

    handleSearch();
  };

  useEffect(() => {
    setFilteredSubscriptions(() =>
      _subscriptions.filter(
        (_, index) =>
          index + 1 > 10 * (pageIndex - 1) && index + 1 <= 10 * pageIndex
      )
    );

    if (query) {
      handleSearch();
    }
  }, [pageIndex, query]);

  const handleSort = (def) => {
    switch (def) {
      case "index": {
        setPageIndex(1);
        if (sorted !== "index") {
          setSorted("index");
        } else {
          setSorted("");
        }

        _subscriptions.reverse();
        setSubscriptions(_subscriptions);
        filterSubscriptions(_subscriptions);
        return;
      }
      case "amount": {
        setPageIndex(1);
        if (sorted === "amount") {
          setSorted("");
          _subscriptions.sort((a, b) => a.index - b.index);
        } else {
          setSorted("amount");
          _subscriptions.sort((a, b) => {
            if (a.amount < b.amount) return -1;
            if (a.amount > b.amount) return 1;
            return 0;
          });
        }
        filterSubscriptions(_subscriptions);
        return;
      }
      case "customer": {
        setPageIndex(1);
        if (sorted === "customer") {
          setSorted("");
          _subscriptions.sort((a, b) => a.index - b.index);
        } else {
          setSorted("customer");
          _subscriptions.sort((a, b) => {
            if (a.payer_name < b.payer_name) return -1;
            if (a.payer_name > b.payer_name) return 1;
            return 0;
          });
        }
        filterSubscriptions(_subscriptions);
        return;
      }
      case "status": {
        setPageIndex(1);
        if (sorted === "status") {
          setSorted("");
          _subscriptions.sort((a, b) => a.index - b.index);
        } else {
          setSorted("status");
          _subscriptions.sort((a, b) => {
            if (a.subscription.status < b.subscription.status) return -1;
            if (a.subscription.status > b.subscription.status) return 1;
            return 0;
          });
        }
        filterSubscriptions(_subscriptions);
        return;
      }
      case "date": {
        setPageIndex(1);
        if (sorted === "date") {
          setSorted("");
          _subscriptions.sort((a, b) => a.index - b.index);
        } else {
          setSorted("date");
          _subscriptions.sort(
            (a, b) => new Date(a.created) - new Date(b.created)
          );
        }
        filterSubscriptions(_subscriptions);
        return;
      }
    }
  };

  return (
    <div className="mt-5 max-h-[83vh] overflow-y-scroll">
      <EarningsChart
        transactions={filteredSubscriptions}
        value="subscriptions"
        subtitle={`All Subscriptions`}
        title={`All Subscriptions Overview`}
        yOriginTitle={`All Subscriptions ($)`}
      />
      <div className="border bg-white rounded-md overflow-x-scroll scrollbar-hide mt-4">
        <div className="flex justify-between p-2 items-center">
          <form className="relative" onSubmit={handleSearchSubmit}>
            <input
              className="outline-none h-[40px] w-[300px] border rounded-md p-2 placeholder placeholder:text-[14px] text-sm"
              placeholder="Search subscription"
              name="query"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button
              className="absolute h-[38px] rounded-md top-1/2 -translate-y-1/2 right-[1px] w-[30px] flex items-center justify-center bg-white cursor-pointer hover:text-primary duration-300"
              type="submit"
            >
              <SearchNormal size={15} />
            </button>
          </form>

          <div className="flex gap-2 items-center">
            <PaginationButton
              item={<ArrowLeft2 size={16} />}
              disabled={pageIndex <= 1}
              handleClick={() => setPageIndex(pageIndex - 1)}
            />
            <span className="font-light text-xs">Page {pageIndex}</span>
            <PaginationButton
              item={<ArrowRight2 size={16} />}
              disabled={pageIndex >= Math.ceil(_subscriptions.length / 10)}
              handleClick={() => setPageIndex((index) => index + 1)}
            />
          </div>
        </div>
        <div className="max-h-[80vh] overflow-y-scroll scrollbar-hide">
          <table className="w-full relative whitespace-nowrap">
            <thead>
              <tr className="uppercase h-14 font-bold bg-[#f1f1f1] text-gray-700 text-[12px]">
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("index")}
                >
                  #{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "index" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td className="px-6 py-4">Consultation ID</td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("amount")}
                >
                  Amount{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "amount" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td className="px-6 py-4">Earning</td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("status")}
                >
                  Status{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "status" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("customer")}
                >
                  Customer{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "customer" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td className="px-6 py-4">Next bill</td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("date")}
                >
                  Date{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "date" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
              </tr>
            </thead>

            <tbody>
              {filteredSubscriptions.length < 1 ? (
                <tr>
                  <td className="px-6 py-4" colSpan={6}>
                    No subscriptions yet
                  </td>
                </tr>
              ) : (
                filteredSubscriptions.map((subscription, index) => (
                  <tr
                    key={subscription.id}
                    className="h-14 text-[14px] border-b"
                  >
                    <td className="px-6 py-4">{subscription.index}</td>
                    <td
                      className="px-6 py-4 underline text-primary cursor-pointer"
                      onClick={() =>
                        navigate(
                          `/dashboard/telemedicine/consultations/${subscription.order_id}`
                        )
                      }
                    >
                      {subscription.order_id}
                    </td>
                    <td className="px-6 py-4">
                      ${subscription.amount.toLocaleString()}
                    </td>
                    <td className="px-6 py-4">
                      $
                      {(
                        subscription.amount - subscription.fee
                      ).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 capitalize">
                      {subscription.subscription.status}
                    </td>
                    <td className="px-6 py-4">{subscription.payer_name}</td>
                    <td className="px-6 py-4">
                      {dayjs(subscription.subscription.current_period_end).format(
                        "DD MMM, YYYY"
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {dayjs(subscription.created_at).format("DD MMM, YYYY")}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
